import { SiteUrls } from '@tableau/shared-urls';
import { DropdownButton,DropdownButtonType } from '@tableau/widgets';
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { defaultMemoize } from 'reselect';

import { MapDispatchToProps, MapStateToProps } from '../../app//utils/ConnectUtils';
import { PageHeaderButtonType } from '../../app/layout/PageHeader';
import { SessionSiteSelectors,SessionUserSelectors } from '../../app/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { AppState } from '../../app/state/AppState';
import { Messages } from '../../codegen/Localize';
import { UserNotificationActions } from '../redux/UserNotificationActions';
import { NotificationCenterStyles } from '../styles/notificationCenterStyles';
import { sendUserNotificationsCenterTelemetry, UserNotificationsTelemetryEvents } from '../telemetry/UserNotificationsTelemetry';
import { Icons } from './Icons';
import { NotificationDisplayMode, UserNotificationPanel } from './NotificationPanel';

/** Manages periodic execution of an action. */
class PeriodicAction {
  private intervalId: NodeJS.Timeout;

  constructor(private action: () => void, private intervalSeconds: number) { }

  /** Starts the periodic execution. Will wait for the wait interval before the first call of action. */
  public start() {
    if (!this.intervalId) {
      this.intervalId = setInterval(this.action, this.intervalSeconds * 1000);
    }
  }

  /** Stops the periodic execution. */
  public stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  /** Calls the action once. */
  public call() {
    this.action();
  }
}

export interface NotificationCenterOwnProps {
  buttonType: PageHeaderButtonType;
}

export interface NotificationCenterPropsFromReduxDispatch {
  readonly userNotificationActions: UserNotificationActions;
}

export interface NotificationCenterPropsFromReduxState {
  readonly nextPageToken: string;
  readonly hasUnseenNotifications: boolean;
  readonly siteUrls: SiteUrls;
  readonly notificationDisplayMode: NotificationDisplayMode;
  readonly userName: string;
  readonly domainName: string;
  readonly userId: string;
  readonly siteLuid: string;
}

export type NotificationCenterComponentProps = NotificationCenterOwnProps & NotificationCenterPropsFromReduxDispatch & NotificationCenterPropsFromReduxState;

export class NotificationCenterComponent extends React.PureComponent<NotificationCenterComponentProps> {
  private checkHasUnseenNotificationPoller: PeriodicAction;
  private dropdownButtonRef: React.RefObject<DropdownButton> = React.createRef();

  constructor(props: NotificationCenterComponentProps) {
    super(props);
    this.checkHasUnseenNotificationPoller =
      new PeriodicAction(() => {(document.visibilityState === 'visible') && this.props.userNotificationActions.checkHasUnseenUserNotifications();}, 60);
  }

  private onDropdownOpen() {
    sendUserNotificationsCenterTelemetry(UserNotificationsTelemetryEvents.openNotificationCenter, this.props.userId, this.props.siteLuid);
    this.props.userNotificationActions.getUserNotifications(this.props.nextPageToken);
  }

  private closePanel = (): void => {
    if (this.dropdownButtonRef.current) {
      sendUserNotificationsCenterTelemetry(UserNotificationsTelemetryEvents.closeNotificationCenter, this.props.userId, this.props.siteLuid);
      this.dropdownButtonRef.current.toggle(false);
      this.props.userNotificationActions.clearUserNotificationsState();
    }
  }

  public render() {

    return (
      <DropdownButton
        aria-controls={'notification-panel'}
        aria-haspopup={'dialog'}
        aria-label={Messages.userNotificationsNotificationCenter()}
        closeOnPopupBlur={true}
        focusOnClose={true}
        useDarkStyling={true}
        ref={this.dropdownButtonRef}
        label={undefined}
        testId={'notification-center'}
        type={DropdownButtonType.Icon}
        children={this.getIconButtonContent()}
        popupContent={<UserNotificationPanel
          siteUrls={this.props.siteUrls}
          userNotificationActions={this.props.userNotificationActions}
          displayMode={this.props.notificationDisplayMode}
          closePanel={this.closePanel}
          userName={this.props.userName}
          domainName={this.props.domainName}
          userId={this.props.userId}
          siteLuid={this.props.siteLuid}
        />}
        className={NotificationCenterStyles.dropdownButton}
        title={Messages.alertsTitle()}
        onOpen={this.onDropdownOpen.bind(this)}
        onClose={this.closePanel}
        popupAlignment={'right'}
      />);
  }

  public componentDidMount() {
    this.checkHasUnseenNotificationPoller.call();
    this.checkHasUnseenNotificationPoller.start();
  }

  public componentWillUnmount() {
    this.checkHasUnseenNotificationPoller.stop();
  }

  private getIconButtonContent(): JSX.Element {
    if (this.props.buttonType === 'light') {
      return this.props.hasUnseenNotifications
        ? <Icons.NotificationCenterLightWithUnseenNotifications testId='notification-center-icon-light-unseen' />
        : <Icons.NotificationCenterLight testId='notification-center-icon-light' />;
    } else {
      return this.props.hasUnseenNotifications
        ? <Icons.NotificationCenterWithUnseenNotifications testId='notification-center-icon-regular-unseen' />
        : <Icons.NotificationCenter testId='notification-center-icon-regular' />;
    }
  }
}

export namespace ReduxConnectedNotificationCenter {

  const mapStateToProps: MapStateToProps<NotificationCenterPropsFromReduxState> = (state: AppState) => ({
    nextPageToken: state.notificationState.nextPageToken,
    hasUnseenNotifications: state.notificationState.hasUnseenNotifications,
    siteUrls: siteUrlsSelector(state),
    notificationDisplayMode: state.notificationState.notificationDisplayMode,
    userName: SessionUserSelectors.sessionUserName(state),
    domainName: SessionUserSelectors.sessionDomainName(state),
    userId: SessionUserSelectors.sessionUserId(state),
    siteLuid: SessionSiteSelectors.siteLuid(state)
  });

  const mapDispatchToProps: MapDispatchToProps<NotificationCenterPropsFromReduxDispatch> = (dispatch: Dispatch<AppState>) => ({
    userNotificationActions: memoizedNotificationActions(dispatch),
  });

  const memoizedNotificationActions = defaultMemoize((dispatch: Dispatch<AppState>) => new UserNotificationActions(dispatch));
  export const HOC = connect(mapStateToProps, mapDispatchToProps);
}


export const NotificationCenter: React.ComponentType<NotificationCenterOwnProps> = ReduxConnectedNotificationCenter.HOC(NotificationCenterComponent);
